import * as Luxon from "luxon";
import { AppointmentTypes, ONLINE_SCHEDULING_TYPE } from "./constants.js";
import ApiUtils from "../../js/api-utils.es6.js";

export default class ScheduleProviderCardUtils {

  static getProviderSpecialtiesArray(data) {
    // reference shared api utils code but leave this link here as its the expected location
    return ApiUtils.getProviderSpecialtiesArray(data);
  }

  static getProviderUrl(data) {
    if (data && data.path && data.path.alias) {
      return data.path.alias;
    }
    return '';
  }

  static getProviderImage(data) {
    let imageUrl = '';
    if (data.field_hero_banner && data.field_hero_banner.field_media_image) {
      imageUrl =
        data.field_hero_banner.field_media_image.field_media_image
          .image_style_uri.webp;
    } else if (data.hero_url && data.hero_url.length > 0) {
      imageUrl = data.hero_url;
    } else {
      imageUrl = '/themes/custom/luminis/images/provider_placeholder_image.svg';
    }
    return imageUrl;
  }

  static getLocationUrl(locationUrlElement) {
    if (locationUrlElement) {
      const parser = new DOMParser();
      const linkElement = parser.parseFromString(locationUrlElement, 'text/html');
      return linkElement.body.firstElementChild.href;
    }
    return '';
  }

  static getLocationImage(heroUrl) {
    let imageUrl = '';
    if (heroUrl.length > 0) {
      imageUrl = heroUrl
    } else {
      imageUrl = '/themes/custom/luminis/images/location_placeholder_image.svg';
    }
    return imageUrl;
  }

  static getLocationPhoneNumber(numberLinkElement) {
    const parser = new DOMParser();
    if (numberLinkElement) {
      const linkElement = parser.parseFromString(numberLinkElement, 'text/html');
      if (linkElement.body.firstElementChild) {
        return linkElement.body.firstElementChild.href.replace('tel:', '');
      }
    }
    return "";
  }

  static getLocationPhoneNumberLink(numberLinkElement) {
    const parser = new DOMParser();
    const linkElement = parser.parseFromString(numberLinkElement, 'text/html');
    if (linkElement.body.firstElementChild) {
      return linkElement.body.firstElementChild.href;
    }
    return "";
  }

  static mapTimeSlotsToDays(availObj, startDateISO, endDateISO, providerFirstSlots, timeSlotsByProvider) {
    // sets start and end date to Eastern time to allow find care to work across time zones
    let startDate = Luxon.DateTime.fromISO(startDateISO, { zone: "America/New_York" });
    let endDate = Luxon.DateTime.fromISO(endDateISO, { zone: "America/New_York" });

    const numDaysTilEndDay = endDate.diff(startDate, ["days"]).values.days;

    const emptySlotObj = {
      accessibility: 'Open',
      visitTypes: [{
          visitTypeId: '',
          appointmentLocation: 'InPerson',
      }],
      slot: '',
      durationMins: 0,
      insuranceRequirement: 'InsuranceOrSelfPay',
      referralRequirement: 'CheckIn',
      hasTelemedicine: false
    }

    // create a list of days between the start and end date to fill out
    let timeSlotByDaysEmpty = {};
    for (let i = 0; i <= numDaysTilEndDay; i++) {
      const date = startDate.plus({ days: i }).toISODate();
      timeSlotByDaysEmpty[date] = [];
    }
    let hasDirectSlots = false;

    // loop over each provider/location and group timeslots by day
    availObj.departmentAvailabilities.forEach(dept => {
      let providerId = availObj.provider ? availObj.provider.id : availObj.departmentAvailabilities[0].department.id;

      // copy but do not use reference
      let timeSlotByDays = JSON.parse(JSON.stringify(timeSlotByDaysEmpty));
      const combinedAvailabilities = ScheduleProviderCardUtils.loopThroughResultsAndCombineVisitTypes(dept);
      let closestTimeSlot = combinedAvailabilities[0];

      // loop over time slots
      combinedAvailabilities.forEach(timeSlot => {
        if (timeSlot.accessibility === 'Direct') {
          hasDirectSlots = true;
        }

        if (timeSlot.slot < closestTimeSlot.slot) {
          closestTimeSlot = timeSlot;
        }
        const day = timeSlot.slot.split('T', 1)[0];
        timeSlotByDays[day].push(timeSlot);
      });

      if (!providerFirstSlots[providerId] || providerFirstSlots[providerId].slot > closestTimeSlot.slot) {
        providerFirstSlots[providerId] = closestTimeSlot;
      }

      // start with a minimum of 4 per location.
      let maxDayCount = 4;
      // loop over timeslots by day and find the max count
      for (const day in timeSlotByDays) {
        if (timeSlotByDays[day].length > maxDayCount) {
          maxDayCount = timeSlotByDays[day].length;
        }
      }

      // loop over timeslots by day and backfill with empty slots as needed.
      for (const day in timeSlotByDays) {
        while (timeSlotByDays[day].length < maxDayCount) {
          timeSlotByDays[day].push(emptySlotObj);
        }
      }
      dept.availabilityByDays = Object.entries(timeSlotByDays);
    });
    availObj.hasDirectSlots = hasDirectSlots;

    if (availObj.provider) {
      timeSlotsByProvider[availObj.provider.id] = availObj;
    } else {
      timeSlotsByProvider[availObj.departmentAvailabilities[0].department.id] = availObj;
    }

    return availObj;
  }

  static loopThroughResultsAndCombineVisitTypes(department) {
    const timeSlotsMap = {};

    // loop over each department
    // "availabilities" is spelled incorrectly in the API as "availablilities"
    department.availablilities.forEach(timeSlot => {
      if (timeSlot.slot in timeSlotsMap) {
        // already exists, combine visit types
        const existingSlot = timeSlotsMap[timeSlot.slot];
        const newVisitTypeObj = {
          visitTypeId: timeSlot.visitTypeId,
          appointmentLocation: timeSlot.appointmentLocation
        }

        const hasTelemedicine = timeSlot.appointmentLocation === AppointmentTypes.TELEMEDICINE;
        existingSlot.visitTypes.push(newVisitTypeObj);

        if (hasTelemedicine) {
          existingSlot.hasTelemedicine = true;
        }
      } else {
        const hasTelemedicine = timeSlot.appointmentLocation === AppointmentTypes.TELEMEDICINE;

        // add the time slot but setup visitTypes as an array.
        timeSlotsMap[timeSlot.slot] = {
          accessibility: timeSlot.accessibility,
          visitTypes: [
            {
              visitTypeId: timeSlot.visitTypeId,
              appointmentLocation: timeSlot.appointmentLocation
            }
          ],
          slot: timeSlot.slot,
          durationMins: timeSlot.durationMins,
          insuranceRequirement: timeSlot.insuranceRequirement,
          referralRequirement: timeSlot.referralRequirement,
          appointmentNotesRequired: timeSlot.appointmentNotesRequired,
          hasTelemedicine: hasTelemedicine,
          provider: timeSlot.provider
        };
      }
    });

    const combinedList = [];
    // convert timeslots map to a flat list again
    Object.values(timeSlotsMap).forEach(value => {
      combinedList.push(value);
    });

    return combinedList;
  }

  static addFilteredSlots(providerAllSlots) {
    const daysWithTelehealth = new Map();
    let containsTelehealth = false;
    let containsInPerson = false;

    providerAllSlots.departmentAvailabilities.forEach(department => {
      const daysWithoutTelehealth = [];
      department.availabilityByDays.forEach(day => {
        const withTelehealth = day[1].filter((appointment) => {
          appointment['deptId'] = department.department.id;

          if (appointment.hasTelemedicine && appointment.slot !== "") {
            containsTelehealth = true;
          } else if (!appointment.hasTelemedicine && appointment.slot !== "") {
            containsInPerson = true;
          }
          return appointment.hasTelemedicine;
        });

        // check if key exists, if it does not push
        if (!daysWithTelehealth.has(day[0])) {
          daysWithTelehealth.set(day[0], withTelehealth);
        } else {
          const existingDay = daysWithTelehealth.get(day[0]);
          const merged = existingDay.concat(withTelehealth);
          daysWithTelehealth.set(day[0], merged);
        }

        const inPerson = day[1].filter((appointment) => !appointment.hasTelemedicine || (appointment.hasTelemedicine && appointment.visitTypes.length > 1));
        daysWithoutTelehealth.push([
          day[0],
          inPerson
        ]);
      })

      // days without telehealth should be per location.
      department['daysWithoutTelehealth'] = daysWithoutTelehealth;
    });

    const sortedTelehealthMap = new Map([...daysWithTelehealth.entries()].sort());
    const flatTelehealthDays = [];
    for (const [key, value] of sortedTelehealthMap.entries()) {
      flatTelehealthDays.push([key, value]);
    }

    // Telehealth has all slots in one place.
    providerAllSlots['hasTelehealthSlots'] = containsTelehealth;
    providerAllSlots['hasInPersonSlots'] = containsInPerson;
    providerAllSlots['daysWithTelehealth'] = flatTelehealthDays;
    return providerAllSlots;
  }

  static async setProviderLocationInfo(providers, userLocation, searchApiService) {
    const locationNids = [...new Set(
      providers
        .map(p => [p.provider_primary_location_nid, ...p.provider_location_nids.split(',')])
        .flat()
        .map(nid => nid.trim())
        .filter(nid => !!nid)
    )];

    const resultsByLocationNid = {};
    const promises = locationNids.map(async (locationNid) => {
      let location;
      if (userLocation) {
        location = (await searchApiService.searchAllLocationsWithDistance('', userLocation, 0, '', locationNid)).rows[0];
      }
      else {
        location = (await searchApiService.searchLocationByNid(locationNid)).rows[0];
      }

      if (location) {
        resultsByLocationNid[locationNid] = location;
      }
    });

    await Promise.allSettled(promises);

    for (const provider of providers) {
      if (provider.provider_primary_location_nid.length > 0) {
        const locationResults = resultsByLocationNid[provider.provider_primary_location_nid];
        if (locationResults) {
          provider.primary_location = locationResults;
        }
      }
      provider.locations = [];
      if (provider.provider_location_nids !== '') {
        const providerLocationNids = provider.provider_location_nids.split(',').map(nid => nid.trim());
        for (const providerLocationNid of providerLocationNids) {
          const locationResults = resultsByLocationNid[providerLocationNid];
          if (locationResults) {
            provider.locations.push(locationResults);
          }
        }
        ScheduleProviderCardUtils.setProviderClosestDistance(provider);
      }
    }
  }

  static setProviderClosestDistance(provider) {
    provider.locations.sort(function(a, b) {
      const aDistanceToNum = parseFloat(a.field_geolocation_distance);
      const bDistanceToNum = parseFloat(b.field_geolocation_distance);
      if (aDistanceToNum < bDistanceToNum) {
        return -1;
      } else if (aDistanceToNum > bDistanceToNum) {
        return  1;
      } else {
        return  0;
      }
    });
  }

  static async setProviderOnlineSchedulingAvailabilityInfo(providers, luminisServiceUtils, exclusionsList) {
    const promises = providers.map(async (provider) => {
      provider.hasOnlineAvailability = false;
      provider.hasInPersonOnlineAvailability = false;
      provider.hasTelemedicineOnlineAvailability = false;

      const isOnlineSchedulingEnabled = ScheduleProviderCardUtils.isOnlineSchedulingEnabled(provider);
      if (isOnlineSchedulingEnabled) {
        const availabilityInfo = await luminisServiceUtils.getProviderOnlineSchedulingAvailabilityInfo(provider.field_provider_npi_number, exclusionsList);
        if (availabilityInfo) {
          provider.hasOnlineAvailability = availabilityInfo.hasAvailability;
          provider.hasInPersonOnlineAvailability = availabilityInfo.hasInPersonAvailability;
          provider.hasTelemedicineOnlineAvailability = availabilityInfo.hasTelemedicineAvailability;
        }
      }
    });

    await Promise.allSettled(promises);
  }

  static isOnlineSchedulingEnabled(provider) {
    const openOnlineSchedulingEnabled = parseInt(provider.count_open_visit_reason_mnemonics) > 0;
    const directOnlineSchedulingEnabled = parseInt(provider.count_direct_visit_reason_mnemonics) > 0;
    return openOnlineSchedulingEnabled || directOnlineSchedulingEnabled;
  }

  static updateTippy(providers) {
    if (providers && providers.length > 0) {
      document.querySelectorAll('.affiliation__icon').forEach(element => {
        tippy(element, {
          content: providers[0].field_affiliated_provider_tooltip,
          trigger: 'mouseenter focus click',
          duration: [250, 200],
          theme: 'light',
          interactive: true,
        });
      });
    }
  }

  static filterProviders(providers, onlineSchedulingType) {
    return providers.filter(p => {
      switch (onlineSchedulingType) {
        case ONLINE_SCHEDULING_TYPE.IN_PERSON:
          return p.hasInPersonOnlineAvailability;
        case ONLINE_SCHEDULING_TYPE.TELEMEDICINE:
          return p.hasTelemedicineOnlineAvailability;
        case ONLINE_SCHEDULING_TYPE.IN_PERSON_OR_TELEMEDICINE:
          return p.hasOnlineAvailability;
        default:
          return true;
      }
    });
  }

  static sortProvidersByIsLuminisProvider(providers) {
    providers.sort(function (a, b) {
      if (!a.field_is_luminis_health_provider && !b.field_is_luminis_health_provider) {
        return 1;
      } else if (a.field_is_luminis_health_provider && !b.field_is_luminis_health_provider) {
        return -1;
      } else {
        const aIsLuminisProviderToNum = parseFloat(a.field_is_luminis_health_provider);
        const bIsLuminisProviderToNum = parseFloat(b.field_is_luminis_health_provider);
        if (aIsLuminisProviderToNum < bIsLuminisProviderToNum) {
          return 1;
        } else if (aIsLuminisProviderToNum > bIsLuminisProviderToNum) {
          return -1;
        } else {
          return 0;
        }
      }
    });
  }

  static sortProvidersByDistance(providers) {
    providers.sort(function (a, b) {
      if (a.locations.length < 1 && b.locations.length < 1) {
        return 1;
      } else if (a.locations.length < 1 && b.locations.length > 0) {
        return 1;
      } else if (a.locations.length > 0 && b.locations.length < 1) {
        return -1;
      } else {
        const aDistanceToNum = parseFloat(a.locations[0].field_geolocation_distance);
        const bDistanceToNum = parseFloat(b.locations[0].field_geolocation_distance);
        if (aDistanceToNum < bDistanceToNum) {
          return -1;
        } else if (aDistanceToNum > bDistanceToNum) {
          return 1;
        } else {
          return 0;
        }
      }
    });
  }
}
